import React from 'react';
import { X, Activity, GitBranch, Clock, Target, BarChart3, AlertCircle } from 'lucide-react';

const DetailsModal = ({ item, onClose }) => {
  if (!item) return null;

  const getHealthColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#eab308';
    if (score >= 30) return '#f97316';
    return '#ef4444';
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-dark-card border border-cyan-accent/40 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
           style={{ boxShadow: '0 0 60px rgba(0, 229, 255, 0.3)' }}
           onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-accent/20 to-purple-accent/20 border-b border-cyan-accent/30 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-cyan-accent mb-1"
                style={{ textShadow: '0 0 20px #00e5ff' }}>
              {item.changeNumber}
            </h2>
            <p className="text-gray-400 text-sm">{item.description}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40 transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Health Status */}
            <div className="bg-dark-border/20 rounded-lg p-4 border border-purple-accent/30">
              <div className="flex items-center gap-2 mb-3">
                <Activity size={18} className="text-purple-accent" />
                <h3 className="text-lg font-semibold text-purple-accent">Health Status</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Status:</span>
                  <span className="font-semibold" style={{ color: getHealthColor(item.healthScore) }}>
                    {item.health}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Score:</span>
                  <span className="text-2xl font-bold" style={{ color: getHealthColor(item.healthScore) }}>
                    {item.healthScore}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Health Checks:</span>
                  <span className="text-white font-semibold">{item.healthCheckCount || 0}</span>
                </div>
                {item.raw_data?.main_entity_health?.active_problems !== undefined && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Active Problems:</span>
                    <span className={`font-semibold ${item.raw_data.main_entity_health.active_problems > 0 ? 'text-red-500' : 'text-lime-accent'}`}>
                      {item.raw_data.main_entity_health.active_problems}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Confidence & Matching */}
            <div className="bg-dark-border/20 rounded-lg p-4 border border-orange-accent/30">
              <div className="flex items-center gap-2 mb-3">
                <Target size={18} className="text-orange-accent" />
                <h3 className="text-lg font-semibold text-orange-accent">Confidence & Matching</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Confidence Score:</span>
                  <span className="text-xl font-bold text-orange-accent">{item.confidenceScore}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Match Status:</span>
                  <span className="text-white text-sm">{item.raw_data?.step2_match_status || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Entity Type:</span>
                  <span className="text-white text-sm">{item.entityType}</span>
                </div>
              </div>
            </div>

            {/* State Information */}
            <div className="bg-dark-border/20 rounded-lg p-4 border border-lime-accent/30">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={18} className="text-lime-accent" />
                <h3 className="text-lg font-semibold text-lime-accent">State Information</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Current State:</span>
                  <span className={`font-semibold ${item.stateColor}`}>{item.state}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">State Count:</span>
                  <span className="text-white">{item.raw_data?.state_count || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Created:</span>
                  <span className="text-white text-sm">{item.date} {item.time}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Opened By:</span>
                  <span className="text-white text-sm">{item.raw_data?.opened_by || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Dependencies */}
            <div className="bg-dark-border/20 rounded-lg p-4 border border-yellow-accent/30">
              <div className="flex items-center gap-2 mb-3">
                <GitBranch size={18} className="text-yellow-accent" />
                <h3 className="text-lg font-semibold text-yellow-accent">Dependencies</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total:</span>
                  <span className="text-xl font-bold text-yellow-accent">{item.totalRelationships}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Upstream:</span>
                  <span className="text-white font-semibold">{item.raw_data?.relationships?.upstream?.length || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Downstream:</span>
                  <span className="text-white font-semibold">{item.raw_data?.relationships?.downstream?.length || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Health Check History */}
          {item.healthCheckHistory && item.healthCheckHistory.length > 0 && (
            <div className="mt-6 bg-dark-border/20 rounded-lg p-4 border border-cyan-accent/30">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 size={18} className="text-cyan-accent" />
                <h3 className="text-lg font-semibold text-cyan-accent">Health Check History</h3>
                <span className="text-sm text-gray-500">({item.healthCheckHistory.length} checks)</span>
              </div>
              <div className="flex gap-1 h-24 items-end">
                {item.healthCheckHistory.slice(-20).map((check, idx) => {
                  const score = check.main_health?.score || 0;
                  const height = (score / 100) * 100;
                  return (
                    <div
                      key={idx}
                      className="flex-1 rounded-t transition-all hover:opacity-80"
                      style={{
                        height: `${height}%`,
                        backgroundColor: getHealthColor(score),
                        boxShadow: `0 0 10px ${getHealthColor(score)}40`
                      }}
                      title={`Score: ${score} - ${check.main_health?.status || 'UNKNOWN'}`}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Description */}
          {item.raw_data?.description && (
            <div className="mt-6 bg-dark-border/20 rounded-lg p-4 border border-gray-500/30">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle size={18} className="text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-300">Description</h3>
              </div>
              <p className="text-gray-400 text-sm whitespace-pre-wrap">{item.raw_data.description}</p>
            </div>
          )}

          {/* Links */}
          <div className="mt-6 flex gap-4">
            {item.servicenowLink && (
              <a
                href={item.servicenowLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-4 py-3 bg-cyan-accent/20 border border-cyan-accent/40 rounded-lg text-cyan-accent font-semibold text-center hover:bg-cyan-accent/30 transition-all"
                style={{ boxShadow: '0 0 20px rgba(0, 229, 255, 0.2)' }}
              >
                Open in ServiceNow
              </a>
            )}
            {item.dynatraceLink && (
              <a
                href={item.dynatraceLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-4 py-3 bg-lime-accent/20 border border-lime-accent/40 rounded-lg text-lime-accent font-semibold text-center hover:bg-lime-accent/30 transition-all"
                style={{ boxShadow: '0 0 20px rgba(178, 255, 89, 0.2)' }}
              >
                Open in Dynatrace
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailsModal;
